import { CircularProgress } from '@mui/material'
import { DataTable } from 'components/Shared/DataTable'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { type GridEventListener, type GridColDef } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  type BadgeCategory,
  type BadgeRewardType,
  type BadgeRewardRenewableType,
} from 'types/api'
import { type components } from 'api/playerPayback/api'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { useGetBadgeRewards } from 'hooks/api/Contests/BadgeReward/useGetBadgeRewards'
import { DeleteBadgeRenderCell } from 'pages/Patrons/PatronAccountDetails/Badges/DeleteBadgeRenderCell'
import { useGetAllCorporateAccounts } from 'hooks/api/useGetAllCorporateAccounts'
import { useGetAllOrganizations } from 'hooks/api/useGetAllOrganizations'
import { useGetAllLicensedEstablishments } from 'hooks/api/LicensedEstablishment/useGetAllLicensedEstablishments'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'

type BadgeReward = components['schemas']['BadgeRewardDTO']

const getSubtypeDisplay = (
  badgeReward: BadgeReward,
  corporateAccounts: Array<components['schemas']['FullCorporateAccountDTO']>,
  organizations: Array<components['schemas']['FullOrganizationDTO']>,
  licensedEstablishments: Array<
    components['schemas']['FullLicensedEstablishmentDTO']
  >
) => {
  switch (badgeReward.subtype) {
    case 'CorporateAccount':
      return (
        corporateAccounts.find(
          (corporateAccount) =>
            corporateAccount.id === badgeReward.corporateAccountId
        )?.name ?? '-'
      )
    case 'Organization':
      return (
        organizations.find(
          (organization) => organization.id === badgeReward.organizationId
        )?.name ?? '-'
      )
    case 'Location': {
      const locationIds = badgeReward.licensedEstablishmentIds ?? []
      if (locationIds.length !== 1) {
        return `${locationIds.length} Location(s)`
      }

      return (
        licensedEstablishments.find(
          (licensedEstablishment) => licensedEstablishment.id === locationIds[0]
        )?.standardName ?? '-'
      )
    }
    default:
      return '-'
  }
}

const getSubtypeSearchValue = (
  badgeReward: BadgeReward,
  corporateAccounts: Array<components['schemas']['FullCorporateAccountDTO']>,
  organizations: Array<components['schemas']['FullOrganizationDTO']>,
  licensedEstablishments: Array<
    components['schemas']['FullLicensedEstablishmentDTO']
  >
) => {
  switch (badgeReward.subtype) {
    case 'CorporateAccount':
      return (
        corporateAccounts.find(
          (corporateAccount) =>
            corporateAccount.id === badgeReward.corporateAccountId
        )?.name ?? ''
      )
    case 'Organization':
      return (
        organizations.find(
          (organization) => organization.id === badgeReward.organizationId
        )?.name ?? ''
      )
    case 'Location': {
      const locationIds = badgeReward.licensedEstablishmentIds ?? []
      return licensedEstablishments
        .filter((licensedEstablishment) =>
          locationIds.includes(licensedEstablishment.id ?? 0)
        )
        .map(
          (licensedEstablishment) => licensedEstablishment.standardName ?? ''
        )
        .join(' ')
    }
    default:
      return ''
  }
}

const getColumns = (category: BadgeCategory): GridColDef[] => [
  { field: 'id', headerName: 'ID', minWidth: 25 },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 150,
    flex: 1,
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 1,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 250,
    flex: 1,
    valueGetter: (params) => {
      const badgeType = (typ: BadgeRewardType) => {
        switch (typ) {
          case 'CheckIn':
            return 'Check-In'
          case 'CheckInStreak':
            return 'Check-In Streak'
          case 'PatronProfile_Anniversary':
            return 'Patron Profile - Anniversary'
          case 'Referral_Count':
            return 'Referral - Count'
          case 'PatronProfile_Address':
            return 'Patron Profile - Address'
          case 'PatronProfile_Birthday':
            return 'Patron Profile - Birthday'
          case 'UniqueLocationVisits':
            return 'Unique Location Visits'
          case 'LocationCheckIn':
            return 'Location Check-In'
          case 'LocationCheckInStreak':
            return 'Location Check-In Streak'
          case 'LocationUniqueCheckIn':
            return 'Unique Location Check-In'
          default:
            return ''
        }
      }

      const badgeRewardType = params.row.type as BadgeRewardType
      return badgeType(badgeRewardType)
    },
  },
  ...(category === 'LocationBadge'
    ? [
        {
          field: 'locations',
          headerName: 'Locations',
          minWidth: 215,
          flex: 1,
        },
      ]
    : []),
  {
    field: 'startDate',
    headerName: 'Start Date',
    minWidth: 100,
    flex: 1,
    valueFormatter: dataTablePromotionDateFormatter(false, 'Start'),
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    minWidth: 100,
    flex: 1,
    valueFormatter: dataTablePromotionDateFormatter(false, 'End'),
  },
  {
    field: 'renewableType',
    headerName: 'Renewable',
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => {
      const renewableType = (typ: BadgeRewardRenewableType) => {
        switch (typ) {
          case 'Daily':
            return 'Daily'
          case 'Weekly':
            return 'Weekly'
          case 'Monthly':
            return 'Monthly'
          case 'Yearly':
            return 'Yearly'
          case 'CheckInStreak':
            return 'Check-In Streak'
          default:
            return 'No'
        }
      }

      const leRenewableType = params.row
        .renewableType as BadgeRewardRenewableType
      return renewableType(leRenewableType)
    },
  },
  ...(category === 'JJBadge'
    ? ([
        {
          field: 'dependentBadgeRewardId',
          headerName: 'Depends On Badge Id',
          minWidth: 200,
          flex: 1,
          valueGetter: (params: { row: BadgeReward }) => {
            return params.row.dependentBadgeRewardId === 0
              ? ''
              : params.row.dependentBadgeRewardId
          },
        },
        {
          field: 'jjPointsAwarded',
          headerName: 'JJ Points to award',
          minWidth: 175,
          flex: 1,
          valueGetter: (params: { row: BadgeReward }) => {
            return params.row.jjPointsAwarded === 0
              ? ''
              : params.row.jjPointsAwarded
          },
        },
      ] satisfies GridColDef[])
    : []),
  {
    field: '__menu__',
    headerName: '',
    align: 'left',
    renderCell: (params) => {
      return <DeleteBadgeRenderCell params={params} />
    },
  },
]

export const BadgesTable = ({ category }: { category: BadgeCategory }) => {
  const navigate = useNavigate()
  const query = useGetBadgeRewards()
  const { currentStateId } = useCurrentStateStore()
  const { data: corporateAccounts } = useGetAllCorporateAccounts()
  const { data: organizations } = useGetAllOrganizations()
  const { data: licensedEstablishments } = useGetAllLicensedEstablishments({
    currentStateId: currentStateId ?? 0,
  })
  const [filteredData, setFilteredData] = useState<BadgeReward[]>()

  if (query.isPending) {
    return <CircularProgress />
  }

  if (query.isError) {
    return <p>An error occured.</p>
  }

  const basePath =
    category === 'LocationBadge'
      ? '/Contests/LocationBadges'
      : '/Contests/JJBadges'

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      navigate(`${basePath}/${params.id}`)
    }
  }

  const rows = query.data
    ?.filter((badgeReward) => badgeReward.category === category)
    .map((badgeReward) => ({
      ...badgeReward,
      locations: getSubtypeDisplay(
        badgeReward,
        corporateAccounts ?? [],
        organizations ?? [],
        licensedEstablishments ?? []
      ),
      locationsSearchValue: getSubtypeSearchValue(
        badgeReward,
        corporateAccounts ?? [],
        organizations ?? [],
        licensedEstablishments ?? []
      ),
    }))

  const columns = getColumns(category)

  return (
    <>
      <GridSearchField<'BadgeRewardDTO'>
        placeholder="Search Badges"
        data={rows ?? []}
        handleFilteredData={setFilteredData}
        searchFields={[
          'name',
          'description',
          'type',
          'startDate',
          'endDate',
          'locations',
          'locationsSearchValue',
        ]}
        fullWidth
      />

      <DataTable
        data-testid="badge-rewards-table"
        columns={columns}
        rows={filteredData ?? rows}
        onCellClick={selectCell}
      />
    </>
  )
}
